/**
 * ValidationPanel Component
 *
 * Displays mission validation results with errors, warnings, and info
 */

import { useState, useEffect } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  X,
} from 'lucide-react';
import { useMission } from '../hooks/useMission';
import { validateMission, getValidationSummary } from '../utils/mission-validator';
import { ValidationLevel } from '../types/settings.types';
import type { ValidationResult } from '../types/settings.types';
import { cn } from '@/lib/utils';

interface ValidationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

export function ValidationPanel({ isOpen, onClose, className }: ValidationPanelProps) {
  const { mission } = useMission();
  const [validation, setValidation] = useState<ReturnType<typeof validateMission> | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<ValidationLevel>>(
    new Set([ValidationLevel.ERROR, ValidationLevel.WARNING])
  );

  // Run validation when mission changes
  useEffect(() => {
    if (mission) {
      const result = validateMission(mission);
      setValidation(result);
    } else {
      setValidation(null);
    }
  }, [mission]);

  const toggleSection = (level: ValidationLevel) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(level)) {
      newExpanded.delete(level);
    } else {
      newExpanded.add(level);
    }
    setExpandedSections(newExpanded);
  };

  if (!isOpen) return null;

  const summary = validation ? getValidationSummary(validation) : { errors: 0, warnings: 0, info: 0 };

  // Group results by level
  const errors = validation?.results.filter((r) => r.level === ValidationLevel.ERROR) || [];
  const warnings = validation?.results.filter((r) => r.level === ValidationLevel.WARNING) || [];
  const info = validation?.results.filter((r) => r.level === ValidationLevel.INFO) || [];

  return (
    <div className={cn('bg-slate-900 border-t border-slate-700', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {validation?.isValid ? (
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400" />
            )}
            <h3 className="text-sm font-semibold text-white">
              Validation Results
            </h3>
          </div>

          {/* Badge Summary */}
          <div className="flex items-center gap-2">
            {summary.errors > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-900/30 text-red-400 text-xs font-medium">
                <AlertCircle className="w-3 h-3" />
                {summary.errors}
              </span>
            )}
            {summary.warnings > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-900/30 text-yellow-400 text-xs font-medium">
                <AlertTriangle className="w-3 h-3" />
                {summary.warnings}
              </span>
            )}
            {summary.info > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-900/30 text-blue-400 text-xs font-medium">
                <Info className="w-3 h-3" />
                {summary.info}
              </span>
            )}
          </div>
        </div>

        <button
          onClick={onClose}
          className="text-slate-400 hover:text-white transition-colors p-1"
          title="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="max-h-64 overflow-y-auto">
        {!validation ? (
          <div className="p-4 text-center text-sm text-slate-400">
            No mission to validate
          </div>
        ) : summary.errors + summary.warnings + summary.info === 0 ? (
          <div className="p-4 text-center">
            <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-sm text-green-400 font-medium">
              Mission is valid!
            </p>
            <p className="text-xs text-slate-400 mt-1">
              No issues found
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {/* Errors Section */}
            {errors.length > 0 && (
              <ValidationSection
                title="Errors"
                level={ValidationLevel.ERROR}
                count={errors.length}
                results={errors}
                isExpanded={expandedSections.has(ValidationLevel.ERROR)}
                onToggle={() => toggleSection(ValidationLevel.ERROR)}
              />
            )}

            {/* Warnings Section */}
            {warnings.length > 0 && (
              <ValidationSection
                title="Warnings"
                level={ValidationLevel.WARNING}
                count={warnings.length}
                results={warnings}
                isExpanded={expandedSections.has(ValidationLevel.WARNING)}
                onToggle={() => toggleSection(ValidationLevel.WARNING)}
              />
            )}

            {/* Info Section */}
            {info.length > 0 && (
              <ValidationSection
                title="Information"
                level={ValidationLevel.INFO}
                count={info.length}
                results={info}
                isExpanded={expandedSections.has(ValidationLevel.INFO)}
                onToggle={() => toggleSection(ValidationLevel.INFO)}
              />
            )}
          </div>
        )}
      </div>

      {/* Footer Status */}
      {validation && (
        <div className="px-4 py-2 border-t border-slate-700 bg-slate-800">
          <p className="text-xs text-slate-400">
            {validation.canUpload ? (
              <span className="text-green-400">✓ Ready to upload to vehicle</span>
            ) : (
              <span className="text-red-400">✗ Fix errors before uploading</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Validation Section Component
 */
interface ValidationSectionProps {
  title: string;
  level: ValidationLevel;
  count: number;
  results: ValidationResult[];
  isExpanded: boolean;
  onToggle: () => void;
}

function ValidationSection({
  title,
  level,
  count,
  results,
  isExpanded,
  onToggle,
}: ValidationSectionProps) {
  const Icon =
    level === ValidationLevel.ERROR
      ? AlertCircle
      : level === ValidationLevel.WARNING
      ? AlertTriangle
      : Info;

  const colorClasses =
    level === ValidationLevel.ERROR
      ? 'text-red-400 bg-red-900/20'
      : level === ValidationLevel.WARNING
      ? 'text-yellow-400 bg-yellow-900/20'
      : 'text-blue-400 bg-blue-900/20';

  return (
    <div>
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2 hover:bg-slate-800 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
          <Icon className={cn('w-4 h-4', colorClasses.split(' ')[0])} />
          <span className="text-sm font-medium text-white">{title}</span>
          <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', colorClasses)}>
            {count}
          </span>
        </div>
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="px-4 pb-2 space-y-2">
          {results.map((result) => (
            <ValidationItem key={result.id} result={result} level={level} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Validation Item Component
 */
interface ValidationItemProps {
  result: ValidationResult;
  level: ValidationLevel;
}

function ValidationItem({ result, level }: ValidationItemProps) {
  const borderColor =
    level === ValidationLevel.ERROR
      ? 'border-red-700/50'
      : level === ValidationLevel.WARNING
      ? 'border-yellow-700/50'
      : 'border-blue-700/50';

  const bgColor =
    level === ValidationLevel.ERROR
      ? 'bg-red-900/10'
      : level === ValidationLevel.WARNING
      ? 'bg-yellow-900/10'
      : 'bg-blue-900/10';

  return (
    <div className={cn('rounded-md border p-3 space-y-1', borderColor, bgColor)}>
      <p className="text-sm text-white">{result.message}</p>
      {result.suggestedFix && (
        <p className="text-xs text-slate-400">
          <span className="font-medium">Fix:</span> {result.suggestedFix}
        </p>
      )}
    </div>
  );
}
